<script lang="ts">
export default {
  name: 'CompaniesAnalysisSearchQueries'
}
</script>

<script lang="ts" setup>
import {computed, reactive, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import { KkDateRangeSelect } from 'src/biz_spa/domains/analysis'
import { useKkDateRangeSelect } from 'src/biz_spa/domains/analysis/composables'
import { calcNotAvailableType } from 'src/biz_spa/domains/analysis/functions'
import { searchQueriesCrossAnalysisTypes, searchQueriesDataType } from 'src/biz_spa/domains/analysis/consts'
import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout.vue'
import CompaniesAnalysisTable from '../../../components/CompaniesAnalysis/Table.vue'
import {useStore} from 'vuex'
import type {
  ReadSearchKeywordImpressionsAggregatesParams,
  ReadSearchKeywordImpressionsPlotsParams
} from 'src/biz_spa/stores'
import { useCompaniesAnalysisSearchQueriesStore} from 'src/biz_spa/stores'
import { useSearchKeywordImpressionLabels } from 'src/biz_spa/domains/management/composables'
import type { Kind } from 'src/biz_spa/types'
import {useFilterModal} from 'src/biz_spa/composables'
import type {ChartData, ChartOptions} from 'chart.js'
import {colors, generateGraphId} from 'src/biz_spa/modules/analysis'
import moment from 'moment'
import {usePagination} from 'src/biz_spa/composables/usePagination'
import {storeToRefs} from 'pinia'
import {useI18n} from 'vue-i18n'
import { KkLoading, KkSelect } from 'src/biz_spa/components/base'
import { KkFilterButton, KkPagination, KkInfo } from 'src/biz_spa/components/ui'
import { KkFilterModal } from 'src/biz_spa/components/projects'
import ChartLine from '../../../components/ChartLine.vue'
import {toNumberLocaleString} from 'src/biz_spa/modules/localeString'
import Icon from '../../../components/Icon.vue'

// Table.vueがTS化されていないのでreadonlyなpropsを渡せないためこのようなユーティリティを一時的に用意
type RemoveReadonly<T> = {
  -readonly [key in keyof T]: T[key]
}

type FormKind = Extract<
  Kind,
  | 'brands'
  | 'franchisees'
  | 'areas'
  | 'user_companies'
  | 'prefectures'
  | 'original_tag_group_a'
  | 'original_tag_group_b'
  | 'original_tag_group_c'
  | 'original_tag_group_d'
  | 'original_tag_group_e'
  | 'search_keyword_label_brandeds'
  | 'search_keyword_label_selecteds'
  | 'stores'
  | 'locales'
>

type FormValueArgs =
  | { kind: Extract<FormKind, 'stores' | 'locales' | 'search_keyword_label_brandeds' | 'search_keyword_label_selecteds'>, id: string }
  | { kind: Exclude<FormKind, 'stores' | 'locales' | 'search_keyword_label_brandeds' | 'search_keyword_label_selecteds'>, id: number }

const { t, te } = useI18n()
const store = useStore()
const route = useRoute()
const queryStore = useCompaniesAnalysisSearchQueriesStore()
const {
  countOnThisPage,
  all,
  aggregatesLoading,
  aggregates,
  plotsLoading,
  plots,
  totalCount,
} = storeToRefs(queryStore)
const {
  readSearchKeywordImpressionsAggregates,
  readSearchKeywordImpressionsPlots,
  getSearchKeywordImpressionsAggregatesCount,
} = queryStore

const gmbSearchKeywordImpressionsAnalysis = store.getters['companiesCompany/gmbSearchKeywordImpressionsAnalysis']
const showOnlyOwnerAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])

// Table.vueがTS化されていないのでreadonlyなpropsを渡せないため
const aggregatesProps = computed<typeof aggregates.value>(() => {
  return aggregates.value.map((aggregate) => ({
    ...aggregate,
    locale: getLocale(aggregate),
  }))
}) as unknown as RemoveReadonly<typeof aggregates.value>

const getLocale = (aggregate: typeof aggregates.value[0]) => {
  if (!aggregate.locale && !aggregate.originalLocale) return ''
  if (aggregate.locale !== 'others') return t(`locales.${aggregate.locale}`)
  if (te(`locales.${aggregate.originalLocale}`)) return `${t('locales.others')}（${t(`locales.${aggregate.originalLocale}`)}）`
  return t('locales.others')
}

const monthRange = [1,3,6,9,12,18]
const lastMonthMomentObj = moment().subtract(1, 'month')
const defaultMonth = lastMonthMomentObj.month() + 1
const defaultYear = lastMonthMomentObj.year()
const {
  startDate,
  endDate,
  selectedYear,
  selectedPeriod,
  selectedMonth,
  setDateRange
} = useKkDateRangeSelect({
  year: defaultYear,
  month: defaultMonth,
  monthRange,
  period: 3,
})
const {
  filterItems,
  filterFormValues,
  initFilterItems,
  resetFilterModal,
  toggleFormValue,
  removeKind,
} = useFilterModal({ kinds: [
  'locales',
  'brands',
  'franchisees',
  'areas',
  'user_companies',
  'search_keyword_label_brandeds',
  'search_keyword_label_selecteds',
  'prefectures',
  'original_tag_group_a',
  'original_tag_group_b',
  'original_tag_group_c',
  'original_tag_group_d',
  'original_tag_group_e',
  'stores',
]})
const {
  currentPage,
  displayNumber,
  sortKey,
  sortOrder,
  setCurrentPage,
  setDisplayNumber,
  setSortKey,
  setSortOrder,
} = usePagination<typeof searchQueriesDataType[number]['dataType']>()
const { readLabels, labels: searchKeywordImpressionLabels } = useSearchKeywordImpressionLabels()

const filteredAggregatesCount = ref(0)

const fetchCount = async () => {
  filteredAggregatesCount.value = await getSearchKeywordImpressionsAggregatesCount({
    companyUuid: companyUuid.value,
    params: readSearchKeywordImpressionsAggregatesQuery.value,
  })
}

const fetchAggregates = async({ resetPlots } = { resetPlots: true }) => {
  await readSearchKeywordImpressionsAggregates({
    companyUuid: companyUuid.value,
    params: readSearchKeywordImpressionsAggregatesQuery.value,
    resetPlots,
  })
  if (!resetPlots) return
  changeDisplayGraphs([generateGraphId(all.value)])
  await Promise.all(displayGraphs.map(graphId => {
    return readSearchKeywordImpressionsPlots({
      companyUuid: companyUuid.value,
      params: readSearchKeywordImpressionsPlotsQuery(graphId),
    })
  }))
}

const enableFilterItems = computed(() => {
  if (!gmbSearchKeywordImpressionsAnalysis) {
    return filterItems.value.filter((item) => item.kind !== 'locales')
  }
  return filterItems.value
})

const viewType = ref<string>('greaterThan15')
const viewTypes = {
  '検索数15件以上のワードを表示': 'greaterThan15',
  '検索数15件未満のワードを表示': 'equalToLessThan15',
}
const changeViewType = (nextViewType: string) => {
  viewType.value = nextViewType
}
watch(viewType, () => {
  if (viewType.value === 'greaterThan15') {
    fetchAggregates()
  } else {
    fetchAggregates({ resetPlots: false })
  }
})

const readSearchAttributes = (companyUuid: string): Promise<void> => store.dispatch('companiesSearchTagGroups/readSearchTagGroups', companyUuid)

const companyUuid = computed<string>(() => route.params.company_uuid as string)
const notAvailableType = computed<string | null>(() => calcNotAvailableType({ vuexStoreName: 'companiesAnalysisSearchQueries' }))
const readSearchKeywordImpressionsAggregatesQuery = computed<ReadSearchKeywordImpressionsAggregatesParams>(() => {
  return {
    analysisAxis: crossSearchFields[0].id,
    locales: filterFormValues.value.locales,
    brandIds: filterFormValues.value.brands,
    franchiseeIds: filterFormValues.value.franchisees,
    areaIds: filterFormValues.value.areas,
    userCompanyIds: filterFormValues.value.user_companies,
    prefectures: filterFormValues.value.prefectures,
    originalTagGroupATagIds: filterFormValues.value.original_tag_group_a,
    originalTagGroupBTagIds: filterFormValues.value.original_tag_group_b,
    originalTagGroupCTagIds: filterFormValues.value.original_tag_group_c,
    originalTagGroupDTagIds: filterFormValues.value.original_tag_group_d,
    originalTagGroupETagIds: filterFormValues.value.original_tag_group_e,
    searchKeywordLabelBranded: filterFormValues.value.search_keyword_label_brandeds,
    searchKeywordLabelSelected: filterFormValues.value.search_keyword_label_selecteds,
    storeUuids: filterFormValues.value.stores,
    startTime: startDate.value,
    endTime: endDate.value,
    sortKey: sortKey.value,
    sortOrder: sortOrder.value,
    page: currentPage.value,
    per: displayNumber.value,
    valueGteq: (viewType.value === 'greaterThan15') ? 15 : null,
    valueLteq: (viewType.value === 'equalToLessThan15') ? 14 : null,
  }
})
const readSearchKeywordImpressionsPlotsQuery = (graphId: string): ReadSearchKeywordImpressionsPlotsParams => {
  const hyphenIndex = graphId.indexOf('-')
  const analysisAxisId = graphId.substring(hyphenIndex + 1)
  return {
    analysisAxis: crossSearchFields[0].id,
    analysisAxisId,
    locales: filterFormValues.value.locales,
    brandIds: filterFormValues.value.brands,
    franchiseeIds: filterFormValues.value.franchisees,
    areaIds: filterFormValues.value.areas,
    userCompanyIds: filterFormValues.value.user_companies,
    prefectures: filterFormValues.value.prefectures,
    originalTagGroupATagIds: filterFormValues.value.original_tag_group_a,
    originalTagGroupBTagIds: filterFormValues.value.original_tag_group_b,
    originalTagGroupCTagIds: filterFormValues.value.original_tag_group_c,
    originalTagGroupDTagIds: filterFormValues.value.original_tag_group_d,
    originalTagGroupETagIds: filterFormValues.value.original_tag_group_e,
    searchKeywordLabelBranded: filterFormValues.value.search_keyword_label_brandeds,
    searchKeywordLabelSelected: filterFormValues.value.search_keyword_label_selecteds,
    storeUuids: filterFormValues.value.stores,
    startTime: startDate.value,
    endTime: endDate.value,
  }
}

const totalPages = computed(() => Math.ceil(totalCount.value / displayNumber.value))
const goPrevPage = async () => {
  setCurrentPage(currentPage.value - 1)
  fetchAggregates({ resetPlots: false })
}
const goNextPage = () => {
  setCurrentPage(currentPage.value + 1)
  fetchAggregates({ resetPlots: false })
}
const changeDisplayNumber = (event: Event) => {
  setDisplayNumber(Number((event.target as HTMLSelectElement).value))
  fetchAggregates({ resetPlots: false })
}

const showFilterModal = ref<boolean>(false)
const handleFilterOpen = () => {
  showFilterModal.value = true
  fetchCount()
}
const handleSubmit = () => {
  setCurrentPage(1)
  fetchAggregates()
  showFilterModal.value = false
}
const handleReset = () => {
  showFilterModal.value = false
  resetFilterModal()

  setCurrentPage(1)
  fetchAggregates()
}
const handleChangeFilterModalCheckBox = ({kind, id}: FormValueArgs): void => {
  if (kind === 'stores' || kind === 'locales' || kind === 'search_keyword_label_brandeds' || kind === 'search_keyword_label_selecteds') {
    toggleFormValue({kind, id})
  } else {
    toggleFormValue({kind, id: id as number})
  }
  fetchCount()
}
const handleCloseFilterModal = () => {
  showFilterModal.value = false
}
const crossSearchFields: typeof searchQueriesCrossAnalysisTypes = reactive([
  ...searchQueriesCrossAnalysisTypes
])
const crossSearchFieldsWithCount = computed(() => crossSearchFields.map(field => ({
  ...field,
  name: `${field.name}(${totalCount.value.toLocaleString()})`
})))
const dataTypes = computed(() => {
  if (!gmbSearchKeywordImpressionsAnalysis) return searchQueriesDataType.filter((dataType) => dataType.id !== 'locale')
  return searchQueriesDataType
})
// Table.vueがTS化されていないのでreadonlyなpropsを渡せないため
const dataTypesProps = dataTypes as unknown as RemoveReadonly<typeof dataTypes.value>

const chartData = computed<ChartData | null>(() => {
  const data = plots.value.filter(plot => displayGraphs.includes(generateGraphId(plot)))
  if (data.length === 0) { return null }
  return {
    labels: Object.keys(plots.value[0].impressionCount).map(v => moment(v).format('Y年M月')),
    datasets: data.map((plot, index) => ({
      label: plot.fields.map(v => v.name).join(' / '),
      data: Object.values(plot.impressionCount),
      backgroundColor: [ 'rgba(255, 255, 255, 0)' ],
      borderColor: [ colors[index % 10] ],
      borderWidth: 2,
      pointBorderColor:  colors[index % 10],
      lineTension: 0,
      pointRadius: 4
    }))
  }
})
const chartOptions = computed<ChartOptions>(() => {
  const y = { beginAtZero: true }
  const x = {}
  return {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 4,
    scales: {
      y,
      x
    },
  }
})

const loading = computed<boolean>(() => aggregatesLoading.value || plotsLoading.value )

let displayGraphs = reactive<string[]>([])
const changeDisplayGraphs = (values: string[]) => {
  displayGraphs.length = 0
  values.forEach(v => {
    displayGraphs.push(v)
  })
}
const handleChangeDisplayGraphs = async(values: string[]) => {
  // 新しく追加されたグラフIDの中でキャッシュがないもののみを抽出して、APIを叩く
  const newValues = values.filter(v => !displayGraphs.includes(v))
  changeDisplayGraphs(values)
  const notCachedGraphs = newValues.filter(graphId => {
    return !plots.value.map(v => generateGraphId(v)).includes(graphId)
  })

  await Promise.all(notCachedGraphs.map(graphId => {
    return readSearchKeywordImpressionsPlots({
      companyUuid: companyUuid.value,
      params: readSearchKeywordImpressionsPlotsQuery(graphId),
    })
  }))
}

const changeIndividualAnalysisField = () => {}

const handleSort = ({ sortId, sortOrder }: { sortId: typeof searchQueriesDataType[number]['id'], sortOrder: 'desc' | 'asc' }) => {
  const data = dataTypes.value.find(v => v.id === sortId)
  if (data == null) throw new Error('存在しないソートキーが指定されました。')
  setSortKey(data.dataType)
  setSortOrder(sortOrder)
  setCurrentPage(1)
  fetchAggregates({ resetPlots: false })
}

const handleChangeDateRange = (payload: { value: number, type: 'year' | 'month' | 'period' }) => {
  setDateRange(payload)
  setCurrentPage(1)
  fetchAggregates()
}

const valueFormatter = (value: unknown) => {
  if (typeof value === 'string') return value ? value : '-'
  return Number(value) !== 0 ? toNumberLocaleString(value) : '< 15'
}

const load = async () => {
  await readLabels()
  await readSearchAttributes(companyUuid.value)
  await resetFilterModal()
  initFilterItems()
  fetchAggregates()

  // ラベルに関する絞り込みはキーワードが設定されている場合のみ表示
  if (searchKeywordImpressionLabels.value) {
    for (const label of searchKeywordImpressionLabels.value) {
      switch (label.id) {
      case 'branded':
        if (!label.keywords.length) removeKind('search_keyword_label_brandeds')
        break
      case 'selected':
        if (!label.keywords.length) removeKind('search_keyword_label_selecteds')
        break
      default:
        break
      }
    }
  }
}
load()
</script>

<template>
  <CompaniesAnalysisLayout
    :not-available-type="notAvailableType"
    :company-uuid="companyUuid"
  >
    <template #header>
      <div class="searchQueryHeader">
        <div class="searchQueryHeader__left">
          <div class="searchQueryHeader__viewType">
            <KkSelect
              :model-value="viewType"
              :options="viewTypes"
              :disabled="loading"
              @update:model-value="changeViewType"
            />
          </div>
          <div class="searchQueryHeader__filterButton">
            <KkFilterButton :disabled="loading" @click="handleFilterOpen" />
          </div>
        </div>
        <div class="searchQueryHeader__right">
          <KkDateRangeSelect
            :loading="aggregatesLoading"
            :selected-year="selectedYear"
            :selected-month="selectedMonth"
            :selected-period="selectedPeriod"
            :ranges="monthRange"
            @on-date-range-changed="handleChangeDateRange"
          />
        </div>
      </div>
    </template>
    <template #overview>
      <div class="p-box__info">
        <div class="c-info">
          <div class="c-info__icon">
            <Icon name="iconQuestion" />
          </div>
          <div class="c-info__content">
            <p>{{ t('pages.companies.analysis.searchQueries.info') }}</p>
          </div>
        </div>
      </div>
    </template>
    <template v-if="viewType === 'greaterThan15'" #graph>
      <div v-if="chartData">
        <ChartLine
          :chart-data="chartData"
          :options="chartOptions"
          :height="360"
        />
      </div>
      <KkLoading v-if="plotsLoading" />
    </template>
    <template #table>
      <KkInfo v-if="showOnlyOwnerAuthority" icon="hint" class="moreInfo">
        {{ t('pages.companies.analysis.searchQueries.labelInfo.part1') }}
        <router-link :to="{ name: 'CompaniesSearchKeywordImpressionLabels', params: { company_uuid: companyUuid } }" class="keywordSettingLink">
          {{ t('pages.companies.analysis.searchQueries.labelInfo.part2') }}
        </router-link>
      </KkInfo>
      <CompaniesAnalysisTable
        :loading="aggregatesLoading"
        :items="aggregatesProps"
        :static-heads="dataTypesProps"
        :cross-search-fields="crossSearchFieldsWithCount"
        :search-items="displayGraphs"
        :truncate-store-name="true"
        :show-table-row-index="false"
        :value-formatter="valueFormatter"
        :is-default-sort="false"
        :enable-field-filtering="false"
        :activate-select-function="viewType === 'greaterThan15'"
        :activate-sort-function="viewType === 'greaterThan15'"
        @change-display-graphs="handleChangeDisplayGraphs"
        @change-individual-analysis-field="changeIndividualAnalysisField"
        @sort="handleSort"
      />
    </template>
    <template #paginator>
      <KkPagination
        :loading="aggregatesLoading"
        :current-page="currentPage"
        :display-number="displayNumber"
        :contents-length="countOnThisPage"
        :total-pages="totalPages"
        @onChangeDisplayNumber="changeDisplayNumber"
        @goPrevPage="goPrevPage"
        @goNextPage="goNextPage"
      />
    </template>
    <template #filter>
      <KkFilterModal
        :is-visible="showFilterModal"
        :filter-items="enableFilterItems"
        :form-values="filterFormValues"
        :total-count="filteredAggregatesCount"
        @close="handleCloseFilterModal"
        @submit="handleSubmit"
        @reset="handleReset"
        @change-check-box="handleChangeFilterModalCheckBox"
      />
    </template>
  </CompaniesAnalysisLayout>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../../stylesheets/biz_spa/foundation/mixin";
.searchQueryHeader {
  width: 100%;
  &__filterButton {
    margin-top: 16px;
  }
  &__right {
    margin-top: 16px;
  }
  @include mq(md) {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 16px 24px;
    align-items: center;
    width: 100%;
    &__left {
      display: flex;
      align-items: center;
    }
    &__right {
      margin-top: 0;
    }
    &__filterButton {
      margin: 0 0 0 24px;
    }
  }
}
.keywordSettingLink {
  color: #E9446A;
}
.filterStoreSection {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 16px 32px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  background-color: #F8F9FA;
  &__text {
    display: flex;
    align-items: center;
  }
  &__firstStoreName {
    margin-right: 8px;
  }
  &__heading {
    font-weight: bold;
  }
  &__storeCount {
    color: $color-accent-primary;
    text-decoration: underline;
  }
}
.c-info__icon {
  display: flex;
  align-items: center;
  padding: 0 8px 0 4px;
}
.moreInfo {
  margin-bottom: 24px;
}
</style>
